import { Select, DatePicker, ConfigProvider, Input, InputNumber, Button } from "antd";
import { MutableRefObject, useRef, ReactNode, SetStateAction, Dispatch, useEffect } from "react";
import { FilterSearchPayload } from "../table/DataTable";
import { debounce } from "ts-debounce";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import dayjs, { Dayjs } from "dayjs";

import 'dayjs/locale/vi';
import { useSavedFilters } from "@/core/stores/filter.store";
import { AddressRepo } from "@/core/repos/address.repo";
import container from "@/lib/di/container";
import { CloseCircleFilled, CloseCircleOutlined } from "@ant-design/icons";
import { dayjsPipe, dayjsPipeFormatted } from "@/core/pipes/dayjs.pipe";
import { useLocation } from "react-router";
import { DEF_DATE_FORMAT, DEF_DATETIME_FORMAT, DEF_MONTH_FORMAT } from "../../utils/global.utils";
dayjs.locale('vi');

const addressRepo = container.get(AddressRepo);

export enum FilterInputType {
    text, select, date, dateRange, number, element, percentage, month
}
export interface FilterOptions {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
}
export interface FilterInputTemplate {
    type: FilterInputType,
    dataIndex?: string,
    monthIndex?: {month: string, year: string},
    rangeIndex?: {from: string, to: string}
    showTime?: boolean
    placeholder?: string,
    secondaryPlaceholder?: string,
    label?: string,
    options?: FilterOptions[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue?: any
    element?: ReactNode
    span?: number
    disabled?: boolean
}
interface FilterBarProps {
    map: Map<string, FilterSearchPayload>,
    setMap: Dispatch<SetStateAction<Map<string, FilterSearchPayload>>>,
    templates: FilterInputTemplate[],
    onChange: (map: Map<string, FilterSearchPayload>, changes: string[])=>void,
    actions?: ReactNode[],
    hideResetButton?: boolean,
    noCache?: boolean
}

interface InternalFilterBarProps {
    map: Map<string, FilterSearchPayload>,
    templates: FilterInputTemplate[],
    onChange: (map: Map<string, FilterSearchPayload>, changes: string[])=>void
    actions: ReactNode[]
    hideResetButton?: boolean
}

export const FilterBarInternal: React.FC<InternalFilterBarProps> = ({onChange, templates, map, actions, hideResetButton}) => {
    const searchMap: MutableRefObject<Map<string, FilterSearchPayload>> = useRef(new Map(map));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleChange(value: any, name: string) {
        searchMap.current.set(name, {key: name, value: value});
        onChange(searchMap.current, []);
    }

    useEffect(() => {
        searchMap.current = new Map(map);
    }, [map])

    function handleMonthChange(index: {month: string, year: string}, value: Dayjs) {
        if (value&&value.isValid()) {
            searchMap.current.set(index.month, {key: index.month, value: (value.month()+1).toString()});
            searchMap.current.set(index.year, {key: index.year, value: value.year().toString()});
        } else {
            searchMap.current.delete(index.month);
            searchMap.current.delete(index.year);
        }
        onChange(searchMap.current, []);
    }

    function handleRangeChange(index: {from: string, to: string}, from: string, to: string, format: string) {

        const formatDate = (date: Dayjs): string => {
            if (!date.isValid()) return "";
            const dateString = date.format();
            const dateNoTZ = dateString.split("+")[0] + "+00:00";
            return dateNoTZ;
        }

        const _from = dayjs(from, format, true);
        const _to = dayjs(to, format, true);
        searchMap.current.set(index.from, {key: index.from, value: formatDate(_from)});
        searchMap.current.set(index.to, {key: index.to, value: formatDate(_to)});
        onChange(searchMap.current, []);
    }

    function parseMonthValue(index: {month: string, year: string}) {
        const month = searchMap.current.get(index.month);
        const year = searchMap.current.get(index.year);
        if (!month||!year) return null;
        return dayjs(`${year.value}-${month.value}-01`);
    }

    function resetFilters() {
        onChange(new Map(), []);
    }

    function getRangeValue(template: FilterInputTemplate): [start: Dayjs | null | undefined, end: Dayjs | null | undefined] {
        const fromString = map.get(template.rangeIndex?.from??"from")?.value;
        const toString = map.get(template.rangeIndex?.to??"to")?.value;
        const fromStringTZ = fromString?fromString.split("+")[0] + "+07:00":null;
        const toStringSTZ = toString?toString.split("+")[0] + "+07:00":null;
        return [
            fromStringTZ?dayjsPipe(fromStringTZ):null,
            toStringSTZ?dayjsPipe(toStringSTZ):null,
        ]
    }

    function getTemplateKey(template: FilterInputTemplate, index: number): string {
        if (template.dataIndex) {
            return template.dataIndex;
        }
        if (template.rangeIndex) {
            return `${template.rangeIndex.from}-${template.rangeIndex.to}`;
        }
        if (template.monthIndex) {
            return `${template.monthIndex.month}-${template.monthIndex.year}`;
        }
        // Fallback to index if no unique identifier is available
        return `filter-${index}`;
    }

    function getElement(template: FilterInputTemplate) {
        switch(template.type) {
            case FilterInputType.text:
                return <Input key={template.dataIndex} placeholder={template.placeholder} value={map.get(template?.dataIndex??"")?.value??""} onChange={(e) => handleChange(e.target.value, template?.dataIndex??"")}/>
            case FilterInputType.select:
                return <Select key={template.dataIndex} showSearch allowClear
                options={(template.options??[]).map(opt => ({value: opt.value, label: opt.name}))}
                filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                className="w-full" placeholder={template.placeholder} onChange={(value)=>handleChange(value, template?.dataIndex??"")} value={map.get(template?.dataIndex??"")?.value??null}>
                    <Select.Option value={null}>Tất cả</Select.Option>
                    {template.options?template.options.map(option => (
                        <Select.Option key={option.value} value={option.value}>{option.name}</Select.Option>
                    )):null}
                </Select>
            case FilterInputType.date:
                return <DatePicker format={DEF_DATE_FORMAT} key={template.dataIndex}  placeholder={template.placeholder} onChange={(_, date)=> handleChange(date, template?.dataIndex??"")}/>
            case FilterInputType.dateRange:
                return <DatePicker.RangePicker
                    format={template.showTime?DEF_DATETIME_FORMAT:DEF_DATE_FORMAT}
                    showTime={template.showTime}
                    value={getRangeValue(template)}
                    style={{width: "100%"}}
                    key={template.rangeIndex?.from??"from"+"-"+template.rangeIndex?.to??"to"}
                    placeholder={[template?.placeholder??"", template?.secondaryPlaceholder??template.placeholder??""]}
                    onChange={(_, range)=> handleRangeChange(template.rangeIndex??{from: "from", to: "to"}, range[0], range[1], template.showTime?DEF_DATETIME_FORMAT:DEF_DATE_FORMAT)}/>
            case FilterInputType.number:
                return <Input key={template.dataIndex} type="number" placeholder={template.placeholder} value={map.get(template?.dataIndex??"")?.value??""} onChange={(e) => handleChange(e.target.value, template?.dataIndex??"")}/>
            case FilterInputType.percentage:
                    return <InputNumber<number>
                    key={template.dataIndex}
                    placeholder={template.placeholder}
                    min={0}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value?.replace('%', '') as unknown as number}
                    onChange={(e) => handleChange((e??0).toString(), template?.dataIndex??"")}
                  />   
            case FilterInputType.element:
                return template.element     
            case FilterInputType.month:
                return <DatePicker.MonthPicker
                    format={DEF_MONTH_FORMAT}
                    key={template.monthIndex?.month??"month"+"-"+template.monthIndex?.year??"year"}
                    className="w-full"
                    placeholder={template.placeholder}
                    onChange={(e) => handleMonthChange(template.monthIndex??{month: "month", year: "year"}, e)}
                    value={parseMonthValue(template.monthIndex??{month: "month", year: "year"})}
                />
        }
    }
    return (
        <div className="text-xs">
            <div className="grid grid-cols-4 gap-x-4 gap-y-6 items-end mt-1 pb-2">
                {templates.filter(t => t.disabled != true).map((template, index) => (
                <div key={getTemplateKey(template, index)} className={`w-full flex flex-col items-start${template.span?" col-span-" + template.span:""}`}>
                    {template.label&&<p className="mb-2 text-[16px] font-[400]">{template.label}</p>}
                    {getElement(template)}
                </div>))}
                {actions}
            </div>
            <div className="flex flex-row justify-start mt-1">
                {!hideResetButton&&<Button
                    type="text"
                    onClick={resetFilters}
                    icon={<CloseCircleFilled />}
                    size="small"
                    style={{
                        fontSize: 13,
                        color: "gray"}}>Xoá bộ lọc</Button>}
            </div>
        </div>
    )
}

export const FilterBar: React.FC<FilterBarProps> = ({onChange, templates, map, actions, hideResetButton, noCache}) => {
    const oldSearchMap:MutableRefObject<Map<string, FilterSearchPayload>> = useRef(new Map());
    const {pathname} = useLocation();
    const savedFilterStore = useSavedFilters();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleChange = (map: Map<string, FilterSearchPayload>, changes: string[]) => {
        const changedFields: string[] = [];
        map.forEach((value, key) => {
            if (oldSearchMap.current.get(key)?.value != value.value) {
                changedFields.push(key);
            }
        })
        oldSearchMap.current = new Map(map);
        savedFilterStore.setPageFilter(pathname, map);
        onChange(map, changedFields);
    }
    
    useEffect(() => {
        if (!noCache) {
            const savedFilterMap = savedFilterStore.savedFilter.get(pathname);
            if (savedFilterMap) {
                handleChange(new Map(savedFilterMap), []);
            }
        }
    }, [pathname])

    return<ConfigProvider theme={MODAL_THEME}>
        <FilterBarInternal onChange={handleChange} templates={templates} map={map} actions={actions??[]} hideResetButton={hideResetButton} />
    </ConfigProvider>
}

export const handleAddressChange = async (
    map: Map<string, FilterSearchPayload>,
    changes: string[],
    filterTemplates: FilterInputTemplate[],
    setFilterTemplates: Dispatch<SetStateAction<FilterInputTemplate[]>>,
    ) => {
    let isDirty = false;
    const templates: FilterInputTemplate[] = [...filterTemplates];

    if (changes.includes("provinceId")) {
        isDirty = true;
        const districts = (await addressRepo.getAllDistricts(map.get("provinceId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
        const districtTemplate = templates.find(template => template.dataIndex === "districtId");
        if (districtTemplate) {
            if (!changes.includes("districtId")) {
                map.delete("districtId");
            }
            districtTemplate.options = districts;
        }
        const wardTemplate = templates.find(template => template.dataIndex === "wardId");
        if (wardTemplate) {
            if (!changes.includes("wardId")) {
                map.delete("wardId");
            }
            const districtId = map.get("districtId")?.value;
            if (districtId) {
                const wards = (await addressRepo.getAllWards(map.get("districtId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
                wardTemplate.options = wards;
            } else {
                wardTemplate.options = [];
            }
        }
    }

    if (changes.includes("districtId")&&!changes.includes("provinceId")) {
        isDirty = true;
        const wards = (await addressRepo.getAllWards(map.get("districtId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
        const wardTemplate = templates.find(template => template.dataIndex === "wardId");
        if (wardTemplate) {
            if (!changes.includes("wardId")) {
                map.delete("wardId");
            }
            wardTemplate.options = wards;
        }
    }

    if (isDirty) {
        setFilterTemplates(templates);
    };
}